import { useMutation, useQuery, useQueryClient } from "react-query";

import { ProjectAIProductID } from "src/reusable/products";
import axios from "axios";
import { nuclearn_api_url } from "../../App";

/*
 * There are still legacy axios calls, these will get removed as functionality
 * is moved to react-query
 */
export class ModelService {
  static train_model(model_id, version_num, callback) {
    axios
      .post(
        `${nuclearn_api_url}/models/${model_id}/version/${version_num}/train/`,
      )
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        console.log(error);
        alert(error);
      });
  }

  static update_model_attribute(model_id, attr, val) {
    let update = {};
    update[attr] = val;
    return axios.post(`${nuclearn_api_url}/models/${model_id}/update/`, update);
  }

  static get_model(model_id, callback, onErrorCallback) {
    ModelService.get_model_promise(model_id)
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => onErrorCallback(error));
  }

  static get_model_promise(model_id) {
    return axios.get(`${nuclearn_api_url}/models/${model_id}/`);
  }

  static updateModelDeploymentStatus(
    update_status_to_bool,
    model_id,
    model_version,
    callback,
  ) {
    let action = update_status_to_bool === true ? "/deploy/" : "/undeploy/";

    axios
      .post(
        `${nuclearn_api_url}/models/${model_id}/version/${model_version}${action}`,
        {},
      )
      .then((response) => {
        callback(response.data);
      })
      .catch((error) => {
        console.log(error);
      });
  }

  static submit_bulkscore_spreadsheet(
    model_id,
    version_num,
    limit_results,
    results_limited_to,
    columns_or_rows,
    include_input_data,
    spreadsheet_to_score,
    onSuccessCallback,
    onErrorCallback,
  ) {
    let formData = new FormData();
    formData.append("spreadsheet_to_score", spreadsheet_to_score);
    axios({
      method: "post",
      url: `${nuclearn_api_url}/models/${model_id}/version/${version_num}/bulkscore/submit?limit_results=${limit_results}&results_limited_to=${results_limited_to}&columns_or_rows=${columns_or_rows}&include_input_data=${include_input_data}`,
      data: formData,
      headers: { "Content-Type": "multipart/form-data" },
    })
      .then((response) => {
        onSuccessCallback(response.data);
      })
      .catch((response) => onErrorCallback(response));
  }

  static get_bulkscore_spreadsheet_status(
    model_id,
    version_num,
    job_id,
    onSuccessCallback,
    onErrorCallback,
  ) {
    axios
      .get(
        `${nuclearn_api_url}/models/${model_id}/version/${version_num}/bulkscore/status?job_id=${job_id}`,
      )
      .then((response) => {
        onSuccessCallback(response.data);
      })
      .catch((response) => onErrorCallback(response));
  }

  static bulkscore_spreadsheet_result(
    model_id,
    version_num,
    job_id,
    onSuccessCallback,
    onErrorCallback,
  ) {
    axios
      .get(
        `${nuclearn_api_url}/models/${model_id}/version/${version_num}/bulkscore/result?job_id=${job_id}`,
      )
      .then((response) => {
        onSuccessCallback(response);
      })
      .catch((response) => onErrorCallback(response));
  }
}

const createModel = (model) => {
  return axios.put(`${nuclearn_api_url}/models/`, model).then((response) => {
    return response.data;
  });
};

export const useCreateModel = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(createModel, {
    onSuccess: () => {
      queryClient.invalidateQueries("getModels");
      queryClient.invalidateQueries("datasetModels");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const getModel = (modelId) => {
  return axios
    .get(`${nuclearn_api_url}/models/${modelId}/`)
    .then((response) => {
      return response.data;
    });
};

export const useGetModel = (modelId) => {
  return useQuery(["getModel", modelId], () => getModel(modelId), {
    enabled: Boolean(modelId),
    staleTime: 1000 * 60 * 5,
    cacheTime: 1000 * 60 * 5,
  });
};

const getModels = (productId, filterNeverDeployed) => {
  let params = {};
  if (productId) {
    params.product_id = productId;
  }
  if (filterNeverDeployed) {
    params.never_deployed = true;
  }
  return axios.get(`${nuclearn_api_url}/models/`, { params }).then((res) => {
    return res.data;
  });
};

export const useGetModels = (productId, filterNeverDeployed) => {
  const queryClient = useQueryClient();
  return useQuery(
    ["getModels", productId],
    () => getModels(productId, filterNeverDeployed),
    {
      onSuccess: (data) => {
        /*
         * Since we are getting all the model data back... let's avoid
         * a bunch of additional requeries
         */
        data.sort((a, b) => {
          // sort by name case insensitive
          return a.name.toLowerCase() < b.name.toLowerCase()
            ? -1
            : a.name.toLowerCase() > b.name.toLowerCase()
              ? 1
              : 0;
        });
        data.forEach((model) => {
          queryClient.setQueryData(["getModel", model.id], model);
          model.versions.forEach((version) => {
            queryClient.setQueryData(
              ["getModelVersion", model.id, version.version_number],
              version,
            );
          });
        });
      },
    },
  );
};

const getExistingMatchingPersonas = (model_id) => {
  return axios
    .get(`${nuclearn_api_url}/chat/match-persona/${model_id}`)
    .then((res) => {
      return res.data;
    });
};

export const useMatchingPersonas = (model_id, modelType) => {
  return useQuery(
    ["matchingPersonas", model_id],
    () => getExistingMatchingPersonas(model_id),
    {
      enabled: modelType === "AtomAssist",
      refetchOnWindowFocus: true,
    },
  );
};

const swapPersonasToModel = ({ fromModelId, toModelId }) => {
  return axios
    .post(`${nuclearn_api_url}/chat/config/swap-model`, {
      from_model_id: fromModelId,
      to_model_id: toModelId,
    })
    .then((response) => {
      return response.data;
    });
};

export const useSwapPersonasToModel = () => {
  const queryClient = useQueryClient();

  return useMutation(swapPersonasToModel, {
    onSuccess: (data, variables) => {
      // The personas linked to both models changed — refresh the delete-blocker
      // check and any cached persona lists.
      queryClient.invalidateQueries([
        "matchingPersonas",
        variables.fromModelId,
      ]);
      queryClient.invalidateQueries(["matchingPersonas", variables.toModelId]);
      // The persona/agent lists are keyed ["atomAssistConfig", ...]; invalidating the
      // string "personas" matched nothing (no query uses that key).
      queryClient.invalidateQueries(["atomAssistConfig"]);
    },
  });
};

const getModelVersionDataset = (model_id, model_version_num, dataset_id) => {
  return axios
    .get(
      `${nuclearn_api_url}/models/${model_id}/version/${model_version_num}/datasets/${dataset_id}`,
    )
    .then((res) => {
      return res.data;
    });
};

export const useModelVersionDataset = (
  model_id,
  model_version_num,
  dataset_id,
) => {
  return useQuery(
    ["modelVersionDataset", model_id, model_version_num, dataset_id],
    () => getModelVersionDataset(model_id, model_version_num, dataset_id),
    {
      refetchOnWindowFocus: false,
      enabled: dataset_id !== "",
    },
  );
};

const getModelVersionDatasets = (model_id, model_version_num) => {
  return axios
    .get(
      `${nuclearn_api_url}/models/${model_id}/version/${model_version_num}/datasets`,
    )
    .then((res) => {
      return res.data;
    });
};

export const useModelVersionDatasets = (model_id, model_version_num) => {
  return useQuery(
    ["modelVersionDatasets", model_id, model_version_num],
    () => getModelVersionDatasets(model_id, model_version_num),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(model_id && model_version_num), // 0s dont matter since none are used as ids in db
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 5,
    },
  );
};

const createBinary = (wheelFile) => {
  let formData = new FormData();
  formData.append("modelbinary", wheelFile);

  return axios({
    method: "put",
    url: `${nuclearn_api_url}/models/binary/`,
    data: formData,
  }).then((response) => {
    return response.data;
  });
};

export const useCreateBinary = () => {
  const mutation = useMutation(createBinary, {
    onSuccess: () => {},
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const createModelVersion = ({ modelId, modelVersion }) => {
  return axios
    .put(`${nuclearn_api_url}/models/${modelId}/version/`, modelVersion)
    .then((response) => {
      return response.data;
    });
};

export const useCreateModelVersion = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(createModelVersion, {
    onSuccess: () => {
      queryClient.invalidateQueries("getModel");
      queryClient.invalidateQueries("getModels");
      queryClient.invalidateQueries("getModelByVersionId");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const getModelVersion = (modelId, modelVersionNum) => {
  return axios
    .get(`${nuclearn_api_url}/models/${modelId}/version/${modelVersionNum}/`)
    .then((response) => {
      return response.data;
    });
};

export const useGetModelVersion = (modelId, modelVersionNum) => {
  return useQuery(
    ["getModelVersion", modelId, modelVersionNum],
    () => getModelVersion(modelId, modelVersionNum),
    { enabled: Boolean(modelVersionNum) && Boolean(modelId) },
  );
};

const deployModel = (modelId, modelVersionNum) => {
  return axios
    .post(
      `${nuclearn_api_url}/models/${modelId}/version/${modelVersionNum}/deploy/`,
    )
    .then((response) => {
      return response.data;
    });
};

export const useDeployModel = (modelId, modelVersionNum) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(() => deployModel(modelId, modelVersionNum), {
    onSuccess: () => {
      // update the cache to is_deployed = true
      const modelVersionQueryKey = [
        "getModelVersion",
        modelId,
        modelVersionNum,
      ];
      const modelVersion = queryClient.getQueryData(modelVersionQueryKey);
      if (modelVersion) {
        modelVersion.is_deployed = true;
        queryClient.setQueryData(modelVersionQueryKey, modelVersion);
      }
      const allModels = queryClient.getQueryData(["getModels", null]);
      if (allModels) {
        for (let i = 0; i < allModels.length; i++) {
          if (allModels[i].id === modelId) {
            for (let j = 0; j < allModels[i].versions.length; j++) {
              if (allModels[i].versions[j].version_number === modelVersionNum) {
                allModels[i].versions[j].is_deployed = true;
              }
            }
          }
        }
      }
      queryClient.setQueryData(["getModels", null], allModels);
      //Invalidate the model and version queries
      queryClient.invalidateQueries(["getModel", modelId]);
      queryClient.invalidateQueries("getModels");
      queryClient.invalidateQueries("getModelByVersionId");
      queryClient.invalidateQueries(modelVersionQueryKey);
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const unDeployModel = (modelId, modelVersionNum) => {
  return axios
    .post(
      `${nuclearn_api_url}/models/${modelId}/version/${modelVersionNum}/undeploy/`,
    )
    .then((response) => {
      return response.data;
    });
};

export const useUnDeployModel = (modelId, modelVersionNum) => {
  const queryClient = useQueryClient();

  const mutation = useMutation(() => unDeployModel(modelId, modelVersionNum), {
    onSuccess: () => {
      // update the cache to is_deployed = true
      const modelVersionQueryKey = [
        "getModelVersion",
        modelId,
        modelVersionNum,
      ];
      const modelVersion = queryClient.getQueryData(modelVersionQueryKey);
      if (modelVersion) {
        modelVersion.is_deployed = false;
        queryClient.setQueryData(modelVersionQueryKey, modelVersion);
      }
      const allModels = queryClient.getQueryData(["getModels", null]);
      if (allModels) {
        for (let i = 0; i < allModels.length; i++) {
          if (allModels[i].id === modelId) {
            for (let j = 0; j < allModels[i].versions.length; j++) {
              if (allModels[i].versions[j].version_number === modelVersionNum) {
                allModels[i].versions[j].is_deployed = false;
              }
            }
          }
        }
      }
      queryClient.setQueryData(["getModels", null], allModels);
      //Invalidate the model and version queries
      queryClient.invalidateQueries(["getModel", modelId]);
      queryClient.invalidateQueries("getModels");
      queryClient.invalidateQueries("getModelByVersionId");
      queryClient.invalidateQueries(modelVersionQueryKey);
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const getModelVersionLogs = (modelId, versionNum, nLines) => {
  return axios
    .get(`${nuclearn_api_url}/models/${modelId}/version/${versionNum}/logs`, {
      params: { n_lines: nLines },
    })
    .then((response) => {
      return response.data;
    });
};

const useGetModelVersionLogs = (modelId, versionNum, nLines, enabled) => {
  return useQuery(
    ["getModelVersionLogs", modelId, versionNum, nLines],
    () => getModelVersionLogs(modelId, versionNum, nLines),
    {
      enabled,
      retry: false,
      refetchOnWindowFocus: false,
    },
  );
};

const downloadModelVersionLogs = (modelId, versionNum, callback) => {
  axios({
    url: `${nuclearn_api_url}/models/${modelId}/version/${versionNum}/logs`,
    params: {
      as_file: true,
    },
    method: "GET",
    //responseType: "blob",
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `modellogs-${modelId}-${versionNum}.txt`);
      document.body.appendChild(link);
      link.click();
    })
    .finally(() => {
      if (callback) {
        callback();
      }
    });
};

const getModelDatasets = (model_id) => {
  if (model_id) {
    return axios
      .get(`${nuclearn_api_url}/models/${model_id}/datasets`)
      .then((res) => {
        return res.data;
      });
  }
};

export const useModelDatasets = (model_id) => {
  return useQuery(
    ["modelDatasets", model_id],
    () => getModelDatasets(model_id),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(model_id),
      staleTime: 1000 * 60 * 5,
      cacheTime: 1000 * 60 * 5,
    },
  );
};

/*
 * TODO: API does not have a route for models datasets union
 * export const useModelsDatasets = (modelIds) => {
 *   return useQuery(
 *     ["modelDatasets", modelIds],
 *     () => getModelDatasets(modelIds),
 *     {
 *       refetchOnWindowFocus: false,
 *       enabled: !!modelIds && modelIds.length > 0,
 *     }
 *   );
 * };
 */

const getModelVersionInputFields = (model_id, model_version_num) => {
  return axios
    .get(
      `${nuclearn_api_url}/models/${model_id}/version/${model_version_num}/fields`,
    )
    .then((res) => {
      return res.data;
    });
};

export const useModelVersionInputFields = (model_id, model_version_num) => {
  return useQuery(
    ["modelVersionInputFields", model_id, model_version_num],
    () => getModelVersionInputFields(model_id, model_version_num),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(model_id) && Boolean(model_version_num),
    },
  );
};

const getAssociatedModelVersionInputFieldsWithSourceDataRecordFields = (
  modelId,
  modelVersionNum,
  datasetId,
) => {
  return axios
    .get(
      `${nuclearn_api_url}/models/${modelId}/version/${modelVersionNum}/associated-data-fields?dataset_id=${datasetId}`,
    )
    .then((res) => {
      return res.data;
    });
};

export const useAssociatedModelVersionInputFieldsWithSourceDataRecordFields = (
  modelId,
  modelVersionNum,
  datasetId,
) => {
  return useQuery(
    [
      "associatedModelVersionInputFieldsWithSourceDataRecordFields",
      modelId,
      modelVersionNum,
      datasetId,
    ],
    () =>
      getAssociatedModelVersionInputFieldsWithSourceDataRecordFields(
        modelId,
        modelVersionNum,
        datasetId,
      ),
    {
      refetchOnWindowFocus: false,
      enabled: datasetId !== "",
    },
  );
};

const postAssociateNewModelDataset = ({ model_id, datasetId }) => {
  return axios.post(
    `${nuclearn_api_url}/models/${model_id}/datasets?dataset_id=${datasetId}`,
  );
};

export const useAssociateNewModelDataset = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(postAssociateNewModelDataset, {
    onSuccess: () => {
      queryClient.invalidateQueries("modelDatasets");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

export const useAssociateModelVersionInputFieldsWithModelVersionDataset =
  () => {
    const queryClient = useQueryClient();

    return useMutation(
      (vars) => {
        const {
          model_id,
          model_version_num: model_version_number,
          modelVersionDatasetTieId: model_version_dataset_id,
          inputFieldMap: mv_field_to_data_field_map,
        } = vars;

        console.log(
          `Associating ${mv_field_to_data_field_map} to ${model_id} version ${model_version_number}`,
        );
        //format to right version for API
        return axios.post(
          `${nuclearn_api_url}/models/${model_id}/version/${model_version_number}/associate-data-fields`,
          mv_field_to_data_field_map,
          { params: { mlmodel_version_dataset_id: model_version_dataset_id } },
        );
      },
      {
        onSuccess: (data, vars) => {
          const { model_id, model_version_num } = vars;
          queryClient.invalidateQueries([
            "modelVersionInputFields",
            model_id,
            model_version_num,
          ]);
        },
        onError: (error) => {
          console.log(error.response);
        },
      },
    );
  };

export const useAssociateNewModelVersionDataset = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (vars) => {
      const {
        model_id,
        model_version_num: model_version_number,
        datasetId: model_dataset_id,
        logInfer,
        limit_results,
        results_limited_to,
      } = vars;

      return axios.post(
        `${nuclearn_api_url}/models/${model_id}/version/${model_version_number}/datasets/${model_dataset_id}`,
        {
          log_inference_to_dataset: logInfer,
          limit_stored_predictions: {
            limit_results,
            results_limited_to,
          },
        },
      );
    },
    {
      onSuccess: (data, vars) => {
        const { model_id, model_version_num } = vars;
        queryClient.invalidateQueries([
          "modelVersionInputFields",
          model_id,
          model_version_num,
        ]);
        queryClient.invalidateQueries([
          "modelVersionDatasets",
          model_id,
          model_version_num,
        ]);
      },
      onError: (error) => {
        console.log(error.response);
      },
    },
  );
};

export const getModelVersionByVersionId = (model_version_id) => {
  return axios
    .get(`${nuclearn_api_url}/models/?model_version_id=${model_version_id}`)
    .then((res) => {
      return res.data[0];
    });
};

export const useGetModelVersionByVersionId = (model_version_id) => {
  return useQuery(
    ["getModelByVersionId", model_version_id],
    () => getModelVersionByVersionId(model_version_id),
    {
      refetchOnWindowFocus: false,
      enabled: Boolean(model_version_id),
    },
  );
};

const deleteModelVersionDatasetAssociation = (
  modelId,
  modelVersionNum,
  datasetId,
) => {
  console.log(
    `Deleting models: ${modelId} version: ${modelVersionNum} dataset: ${datasetId}`,
  );
  return axios
    .delete(
      `${nuclearn_api_url}/models/${modelId}/version/${modelVersionNum}/dataset?dataset_id=${datasetId}`,
    )
    .then(() => {
      console.log(
        `Deleted modelId ${modelId} modelversionnum${modelVersionNum} datasetId ${datasetId} association.`,
      );
    });
};

export const useDeleteModelVersionDatasetAssociation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (vars) => {
      const { modelId, modelVersionNum, datasetId } = vars;
      return deleteModelVersionDatasetAssociation(
        modelId,
        modelVersionNum,
        datasetId,
      );
    },
    {
      onSuccess: (data, vars) => {
        const { modelId, modelVersionNum } = vars;
        queryClient.invalidateQueries([
          "modelVersionInputFields",
          modelId,
          modelVersionNum,
        ]);
        queryClient.invalidateQueries([
          "modelVersionDatasets",
          modelId,
          modelVersionNum,
        ]);
      },
      onError: (error) => {
        console.log(error.response);
      },
    },
  );
};

const deleteModelDatasetAssociation = (modelId, datasetId) => {
  return axios
    .delete(
      `${nuclearn_api_url}/models/${modelId}/datasets?dataset_id=${datasetId}`,
    )
    .then(() => {
      console.log(
        `Deleted modelId ${modelId} datasetId ${datasetId} association.`,
      );
    });
};

export const useDeleteModelDatasetAssociation = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (vars) => {
      const { modelId, datasetId } = vars;
      return deleteModelDatasetAssociation(
        modelId,

        datasetId,
      );
    },
    {
      onSuccess: (data, vars) => {
        const { modelId } = vars;
        queryClient.invalidateQueries(["modelDatasets", modelId]);
        queryClient.invalidateQueries(["modelVersionDatasets", modelId]);
        queryClient.invalidateQueries(["modelVersionInputFields", modelId]);
      },
      onError: (error) => {
        console.error(error.response);
      },
    },
  );
};

const patchModelVersion = ({ modelId, modelVersion, modelVersionData }) => {
  return axios({
    method: "patch",
    url: `${nuclearn_api_url}/models/${modelId}/version/${modelVersion}`,
    data: modelVersionData,
  });
};

export const usePatchModelVersion = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ modelId, modelVersion, modelVersionData }) =>
      patchModelVersion({ modelId, modelVersion, modelVersionData }),
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries([
        "getModelVersion",
        variables.modelId,
        variables.modelVersion,
      ]);
    },
  });
};

export const _inferFromModel = (modelId, modelVersionNum, inferData) => {
  // Run Inference against a model route
  console.log("Infer from model");
  console.log(modelId, modelVersionNum, inferData);
  let postData = {
    ...inferData,
  };
  console.log("postdata", postData);
  return axios
    .post(
      `${nuclearn_api_url}/models/${modelId}/version/${modelVersionNum}/infer/`,
      postData,
    )
    .then((response) => {
      return response.data;
    });
};

export const _inferFromModelWithSearch = (
  modelId,
  modelVersionNum,
  inferData,
  datasetsForSearchContext,
) => {
  console.log("Infer from model");
  console.log(modelId, modelVersionNum, inferData, datasetsForSearchContext);
  let dataset_fks = datasetsForSearchContext.map((dataset) => dataset.value.id);

  let postData = {
    model_inference_data: inferData,
    search_datasets: dataset_fks,
  };

  const searchUrl = `${nuclearn_api_url}/search/search-enhanced-infer?model_id=${modelId}&version_number=${modelVersionNum}`;

  return axios.post(searchUrl, postData).then((response) => {
    return response.data;
  });
};

export const useInferFromModel = (onSuccess, onError) => {
  return useMutation(
    (vars) => {
      const {
        model_id: modelId,
        model_version: modelVersionNum,
        infer_data: inferData,
        datasetsForSearchContext,
      } = vars;

      if (datasetsForSearchContext && datasetsForSearchContext.length > 0) {
        return _inferFromModelWithSearch(
          modelId,
          modelVersionNum,
          inferData,
          datasetsForSearchContext,
        );
      }
      return _inferFromModel(modelId, modelVersionNum, inferData);
    },
    {
      onSuccess: (data, variables) => {
        console.log("Infer from model success");
        onSuccess(data, variables);
      },
      onError: (error) => {
        console.log(error.response);
        onError(error);
      },
    },
  );
};

const trainWorkManagementModel = (datasetId, actualProjects, modelId) => {
  const hyperparams = {}; // optional hyperparams for training Work Management Quantile Model(s)
  return axios
    .post(`${nuclearn_api_url}/products/projectai/train`, hyperparams, {
      params: {
        dataset_id: datasetId,
        actual_projects: JSON.stringify(actualProjects),
        model_id: modelId,
      },
    })
    .then((res) => res.data);
};

export const useTrainWorkManagementModel = () => {
  const queryClient = useQueryClient();
  const mutation = useMutation(
    ({ datasetId, actualProjects, modelId }) =>
      trainWorkManagementModel(datasetId, actualProjects, modelId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries("modelVersions");
        queryClient.invalidateQueries(["datasetModels"]); // refetches models on training modal
        queryClient.invalidateQueries(["getModels", ProjectAIProductID]);
      },
      onError: (error) => {
        console.log(error.response);
        // if training failed, need to make sure we have latest models that might have been created
        queryClient.invalidateQueries(["getModels", ProjectAIProductID]); // 5 is product id for ProjectAI, refetches models in ProjectSelector.js
        queryClient.invalidateQueries(["datasetModels"]);
      },
    },
  );
  return mutation;
};

const getModelVersions = (modelId) => {
  return axios
    .get(`${nuclearn_api_url}/models/${modelId}/version/`)
    .then((res) => res.data);
};

export const useGetModelVersions = (modelId) => {
  return useQuery(["modelVersions", modelId], () => getModelVersions(modelId), {
    refetchOnWindowFocus: false,
    enabled: Boolean(modelId),
  });
};

const syncRemoteModel = (modelId) => {
  return axios
    .post(`${nuclearn_api_url}/models/${modelId}/sync-remote/`)
    .then((response) => {
      return response.data;
    });
};

export const useSyncRemoteModel = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(syncRemoteModel, {
    onSuccess: () => {
      queryClient.invalidateQueries("getModel");
      queryClient.invalidateQueries("getModels");
      queryClient.invalidateQueries("getModelByVersionId");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

const deleteModelVersion = ({ modelId, modelVersionNum }) => {
  return axios
    .delete(`${nuclearn_api_url}/models/${modelId}/version/${modelVersionNum}/`)
    .then((response) => {
      return response.data;
    });
};

const useDeleteModelVersion = ({ modelId, modelVersionNum }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteModelVersion({ modelId, modelVersionNum }),
    onSuccess: () => {
      queryClient.invalidateQueries(["getModel", modelId]);
      queryClient.invalidateQueries("getModels");
      queryClient.invalidateQueries("getModelByVersionId");
    },
  });
};

const deleteModel = ({ modelId }) => {
  return axios
    .delete(`${nuclearn_api_url}/models/${modelId}/`)
    .then((response) => {
      return response.data;
    });
};

export const useDeleteModel = ({ modelId }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteModel({ modelId }),
    onSuccess: () => {
      queryClient.invalidateQueries("getModels");
    },
  });
};

const getAllModelTags = () => {
  return axios.get(`${nuclearn_api_url}/models/tags`).then((res) => res.data);
};

export const useGetAllModelTags = () => {
  return useQuery("allModelTags", getAllModelTags, {
    refetchOnWindowFocus: false,
  });
};

const updateRemoteModelUrl = ({ modelId, url }) => {
  return axios
    .patch(`${nuclearn_api_url}/models/${modelId}/remote-url/`, { url })
    .then((response) => {
      return response.data;
    });
};

export const useUpdateRemoteModelUrl = () => {
  const queryClient = useQueryClient();

  const mutation = useMutation(updateRemoteModelUrl, {
    onSuccess: () => {
      queryClient.invalidateQueries("getModel");
      queryClient.invalidateQueries("getModels");
      queryClient.invalidateQueries("getModelByVersionId");
    },
    onError: (error) => {
      console.log(error.response);
    },
  });
  return mutation;
};

// Upstream Sync Settings (for remote models)
const getUpstreamSyncInfo = (modelId) => {
  return axios
    .get(`${nuclearn_api_url}/models/${modelId}/upstream-sync/`)
    .then((response) => response.data);
};

export const useGetUpstreamSyncInfo = (modelId) => {
  return useQuery(
    ["upstreamSyncInfo", modelId],
    () => getUpstreamSyncInfo(modelId),
    {
      enabled: Boolean(modelId),
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
  );
};

const updateUpstreamSyncSettings = ({ modelId, syncEnabled }) => {
  return axios
    .patch(`${nuclearn_api_url}/models/${modelId}/upstream-sync/`, {
      sync_enabled: syncEnabled,
    })
    .then((response) => response.data);
};

export const useUpdateUpstreamSyncSettings = () => {
  const queryClient = useQueryClient();

  return useMutation(updateUpstreamSyncSettings, {
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(["upstreamSyncInfo", variables.modelId]);
      queryClient.invalidateQueries("getModel");
    },
  });
};
